import React from 'react';
import ReactECharts from 'echarts-for-react';
import { PercentUpIcon, PercentDownIcon } from '../../../../assets/icon';
import type { RevenueReportData, RevenueMetric } from '../../../../types/chat-message';
import { ReportActionButtons } from './ReportActionButtons';
import { ReportTitle } from './ReportTitle';
import './RevenueReport.scss';

export const CHART_COLORS = ['#8561C8', '#EF9F62', '#FF7174', '#C9C8CF'];

interface RevenueReportProps {
  data: RevenueReportData;
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
  isLastMessage?: boolean;
  isFromHistory?: boolean;
  text?: any;
  buttons?: any;
}

const MetricIndicator: React.FC<{ isUp: boolean; rate?: number }> = ({ isUp, rate }) => (
  <>
    <span className="rr-metric-direction-icon">
      {isUp ? <PercentUpIcon /> : <PercentDownIcon />}
    </span>
    <span className={`rr-metric-change ${isUp ? 'up' : 'down'}`}>
      {rate?.toFixed(2)}%
    </span>
  </>
);

const MetricRow: React.FC<{ label: string; metric: RevenueMetric }> = ({ label, metric }) => {
  const isUp = metric?.direction === 'up';
  const isDown = metric?.direction === 'down';
  const hasIndicator = isUp || isDown;

  return (
    <div className="rr-metric-row">
      <div className="rr-metric-label-group">
        <span className="rr-metric-label">{label}</span>
        {hasIndicator && <MetricIndicator isUp={isUp} rate={metric?.rate} />}
      </div>
      <span className="rr-metric-value">{(metric?.formattedValue ?? '').replace(' VND', ' đ')}</span>
    </div>
  );
};

export const formatTooltipContent = (amount: any): string => {
  const num = typeof amount === 'number' ? amount : parseFloat(String(amount ?? '0'));
  const formattedVal = (isNaN(num) ? 0 : Math.round(num)).toLocaleString('vi-VN');
  return `<div class="rr-chart-tooltip"><div class="rr-chart-tooltip-text">${formattedVal}</div><div class="rr-chart-tooltip-arrow"></div></div>`;
};

export const getTooltipPosition = (point: number[], _p: any, _d: any, _r: any, size: any) => {
  const [w, h] = size?.contentSize ?? [100, 32];
  const [vw, vh] = size?.viewSize ?? [320, 180];
  const cx = point?.[0] ?? vw * 0.58;
  const cy = point?.[1] ?? vh * 0.35;
  const left = Math.max(8, Math.min(cx - 18, vw - w - 8));
  const top = Math.max(4, cy - h - 6);
  return [left, top];
};

const getTooltipOption = () => ({
  trigger: 'item',
  confine: true,
  position: getTooltipPosition,
  backgroundColor: 'transparent',
  borderColor: 'transparent',
  borderWidth: 0,
  padding: 0,
  extraCssText: 'box-shadow: none; z-index: 10;',
  formatter: (params: any) => formatTooltipContent(params?.data?.amount),
});

const PIE_EMPHASIS = {
  scale: true,
  scaleSize: 10,
  label: { show: false },
  itemStyle: {
    borderColor: '#ffffff',
    borderWidth: 6,
  },
};

const getPieSeriesOption = (chartData: any[]) => ({
  type: 'pie',
  radius: ['42%', '78%'],
  center: ['50%', '50%'],
  startAngle: 90,
  avoidLabelOverlap: false,
  label: { show: false },
  padAngle: 0,
  itemStyle: { borderWidth: 0 },
  emphasis: PIE_EMPHASIS,
  data: chartData,
});

export const getPieChartOption = (chartData: any[]) => ({
  tooltip: getTooltipOption(),
  grid: { containLabel: true },
  series: [getPieSeriesOption(chartData)],
});

export interface RevenueChartDataItem {
  name: string;
  value: number;
  amount: number | string;
  quantity: number | string;
  selected?: boolean;
  itemStyle: { color: string };
  emphasis?: {
    itemStyle?: {
      borderColor?: string;
      borderWidth?: number;
    };
  };
}

const parseAmount = (amount: any): number =>
  typeof amount === 'number' ? amount : parseFloat(String(amount ?? '0')) || 0;

export const isSmallSlice = (val: number, total: number): boolean =>
  total > 0 && val / total <= 0.12;

export const getItemEmphasis = (isSmall: boolean) => ({
  itemStyle: {
    borderColor: '#ffffff',
    borderWidth: isSmall ? 2 : 6,
  },
});

const buildSingleChartItem = (p: any, i: number, total: number): RevenueChartDataItem => {
  const val = parseAmount(p?.amount);
  return {
    name: p?.name ?? '',
    value: val,
    amount: p?.amount ?? 0,
    quantity: p?.quantity ?? 0,
    itemStyle: { color: CHART_COLORS[i % CHART_COLORS.length] },
    emphasis: getItemEmphasis(isSmallSlice(val, total)),
  };
};

export const buildChartData = (topProducts: any[]): RevenueChartDataItem[] => {
  if (!Array.isArray(topProducts)) return [];
  const total = topProducts.reduce((sum, p) => sum + parseAmount(p?.amount), 0);
  return topProducts.map((p, i) => buildSingleChartItem(p, i, total));
};

const ProductLegendItem: React.FC<{ product: any; index: number }> = ({ product, index }) => (
  <div className="rr-legend-item">
    <span
      className="rr-legend-dot"
      style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
    />
    <span className="rr-legend-name">{product?.name ?? ''}</span>
    <span className="rr-legend-qty">{product?.quantity ?? 0}</span>
  </div>
);

const ProductLegendList: React.FC<{ products: any[] }> = ({ products }) => (
  <div className="rr-legend">
    {products.map((p, i) => (
      <ProductLegendItem key={i} product={p} index={i} />
    ))}
  </div>
);

const RevenueChartSection: React.FC<{ topProducts: any[] }> = ({ topProducts }) => (
  <>
    <div className="rr-divider" />
    <span className="rr-products-label">Sản phẩm bán chạy</span>
    <ReactECharts
      option={getPieChartOption(buildChartData(topProducts))}
      style={{ height: '180px', width: '100%' }}
      opts={{ renderer: 'svg' }}
    />
    <ProductLegendList products={topProducts} />
  </>
);



const RevenueCardMetrics: React.FC<{
  revenue?: RevenueMetric;
  orders?: RevenueMetric;
  avgOrder?: RevenueMetric;
}> = ({ revenue, orders, avgOrder }) => (
  <div className="rr-metrics">
    {revenue && <MetricRow label="Doanh thu thuần" metric={revenue} />}
    {orders && <MetricRow label="Đơn hàng" metric={orders} />}
    {avgOrder && <MetricRow label="Trung bình/đơn" metric={avgOrder} />}
  </div>
);

interface RevenueCardProps {
  revenue?: RevenueMetric;
  orders?: RevenueMetric;
  avgOrder?: RevenueMetric;
  topProducts: any[];
}

const RevenueCard: React.FC<RevenueCardProps> = ({ revenue, orders, avgOrder, topProducts }) => {
  const hasMetrics = Boolean(revenue || orders || avgOrder);
  const hasProducts = Array.isArray(topProducts) && topProducts.length > 0;
  if (!hasMetrics && !hasProducts) return null;

  return (
    <div className="rr-card">
      <RevenueCardMetrics revenue={revenue} orders={orders} avgOrder={avgOrder} />
      {hasProducts && <RevenueChartSection topProducts={topProducts} />}
    </div>
  );
};

const getDateLabel = (start?: string, end?: string): string => {
  if (!start && !end) return '';
  return start === end ? (start ?? '') : `${start} - ${end}`;
};

const RevenueReportHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className="rr-header">
    <ReportTitle text={title} />
  </div>
);

interface RevenueReportButtonsProps {
  buttons?: any;
  isDisabled: boolean;
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
}

const RevenueReportButtons: React.FC<RevenueReportButtonsProps> = ({
  buttons,
  isDisabled,
  onButtonClick,
}) => (
  <ReportActionButtons
    buttons={buttons}
    isDisabled={isDisabled}
    onButtonClick={onButtonClick}
    reportType="Doanh thu bán hàng"
    suggestionTitle="Bạn có muốn xem thời gian khác không?"
  />
);

export const RevenueReport: React.FC<RevenueReportProps> = (props) => {
  const { data, buttons, onButtonClick, isLastMessage, isFromHistory, text } = props;
  const isButtonDisabled = !isLastMessage || !!isFromHistory;
  const title = text || `DOANH THU BÁN HÀNG ${getDateLabel(data?.startDate, data?.endDate)}`;

  return (
    <div className="revenue-report">
      <RevenueReportHeader title={title} />
      <RevenueCard revenue={data?.revenue} orders={data?.orders} avgOrder={data?.avgOrder} topProducts={data?.topProducts ?? []} />
      <RevenueReportButtons buttons={buttons} isDisabled={isButtonDisabled} onButtonClick={onButtonClick} />
    </div>
  );
};
